import type { IdeaId } from '../../core/catalog';
import posthog from 'posthog-js';
import { LABS_EXCEPTION_CAPTURE, scrubExceptionEvent } from '../../../shared/exception-scrubber';
import { createSubscriptionEnricher, type GetToken } from '../../../shared/sgapp-subscription';
import type { AnalyticsEvent } from '../../core/events';
import type { FrontDoorVariant } from '../../core/frontDoor';
import type { StampCardVariant } from '../../core/stamps';
import { hasSuggestionBox } from '../../core/suggestions';

/**
 * The browser-side analytics adapter.
 *
 * ONE VENDOR. Statsig was removed from this package on 2026-09-14, the last one in the
 * repo still carrying it; `packages/objectives` dropped it on 2026-09-12. It resolved a
 * gate and a copy experiment that were never created in its console, so it answered
 * "unrecognized" to both while logging a duplicate copy of every event that nothing
 * read — the failure `CLAUDE.md` records under User Events. Worse, awaiting its
 * `initializeAsync()` delayed every event on the page, including `preview_viewed`.
 *
 * Two rules, both load-bearing:
 *
 * 1. **Production has a publishable default.** Production shares sgapp-client's public
 *    PostHog client credential. An explicit `VITE_*` value overrides it. Local
 *    development and tests still use the null adapter when no override is set.
 * 2. **A vendor cannot break a signup.** Every call is wrapped. A failed capture is a
 *    lost data point; a thrown capture would be a lost teacher.
 */

/**
 * The landing layout experiment. E16 reads `labs-landing-style`: editorial_bench against
 * today's page. E10 read `preview-landing-layout` (School Day, Lab Bench) and ended
 * 2026-10-28. `control` and an unassigned visitor both get the editorial page.
 */
export const LANDING_LAYOUT_FLAG = 'labs-landing-style';
export type LandingLayoutVariant = 'control' | 'school_day' | 'lab_bench' | 'editorial_bench';
const LANDING_LAYOUT_VARIANTS: readonly string[] = ['control', 'school_day', 'lab_bench', 'editorial_bench'];

/**
 * The front-door experiment (E13): `question` shows "What are you trying to figure out?"
 * above the listings, in whichever layout E10 assigned. It runs across E10, not inside it.
 * `control`, and anyone unassigned or unresolved, gets the page without it: the
 * experiment fails CLOSED to today's page.
 */
export const FRONT_DOOR_FLAG = 'labs-front-door';
const FRONT_DOOR_VARIANTS: readonly string[] = ['control', 'question'];

/**
 * The stamp card experiment (E17): `stamps` adds the Lab Notebook strip below the listings
 * for a signed-in visitor. Fails CLOSED: unset, failed or late means no card and no
 * exposure, and a visitor whose stamp read fails is exposed in neither arm.
 */
export const STAMP_CARD_FLAG = 'labs-stamp-card';
const STAMP_CARD_VARIANTS: readonly string[] = ['control', 'stamps'];


export const OSA_VISIBLE_FLAG = 'preview-osa-visible';
export const OBJECTIVES_VISIBLE_FLAG = 'preview-objectives-visible';

/** Listing only; hidden until explicitly enabled, separate from rubric access. */
export const RUBRIC_VISIBLE_FLAG = 'preview-rubric-visible';
export type ListingAudience = { publicRolloutPercent: number } | 'unknown';

/**
 * Whether Preview lists 240 Sites.
 *
 * A gate, and it fails CLOSED — `CLAUDE.md` asks which one you built, and this is the
 * case where the preference for a kill switch does not apply: the tool is not ready for
 * everyone, so an unresolved flag must leave it off the page rather than announce it.
 * That is the same shape as `OBJECTIVES_VISIBLE_FLAG`, for the same reason.
 *
 * One question only: it decides whether Preview *lists* the tool. Whether a teacher may
 * use it is 240 Sites' own business — it authenticates her itself — and reusing this
 * flag to answer that would inherit whatever rollout this one is at.
 */
export const SITES_VISIBLE_FLAG = 'preview-sites-visible';

/**
 * Whether Preview lists 240 Marketplace. Same shape as Sites: a gate, fails closed. There
 * is no product behind the card yet, only a notify-me, so an unresolved flag must not
 * announce it.
 */
export const MARKETPLACE_VISIBLE_FLAG = 'preview-marketplace-visible';

/**
 * Whether Labs lists 240 Collab. A gate, and it fails CLOSED, the same shape as Objectives
 * and Sites.
 *
 * ONE QUESTION ONLY: it decides whether this page *lists* Collab. Whether a teacher may use
 * a group is decided inside Collab, by membership, and Collab's Worker never reads this
 * flag — it reads `preview-collab-disabled`, a kill switch that fails open. Reusing a
 * listing flag to answer an access question inherits whatever rollout the listing is at,
 * which is the failure `CLAUDE.md` records against `preview-objectives-visible`.
 *
 * Read through `watchListing`, like Sites and Marketplace, so staff who see the card also
 * see the badge saying why. The flag carries no `publicRolloutPercent` payload today, so
 * the audience resolves to `'unknown'` and the badge reads "Rollout unknown" — the honest
 * answer. Nothing here invents a percentage; add `{"publicRolloutPercent": 0}` to the
 * flag's true payload and it reads "Internal only" instead. See `docs/preview-rollouts.md`.
 */
export const WRITING_FEEDBACK_VISIBLE_FLAG = 'preview-writing-feedback-visible';

export const COLLAB_VISIBLE_FLAG = 'preview-collab-visible';

/**
 * Whether Labs lists 240 Planner. A gate, and it fails CLOSED. ONE QUESTION ONLY: the
 * listing. Whether a teacher may use the Planner is decided by its own Worker, which reads
 * `preview-planner-disabled` (a kill switch that fails open) and never this flag.
 */
export const PLANNER_VISIBLE_FLAG = 'preview-planner-visible';

/**
 * Whether Labs lists 240 Profile. A gate, and it fails CLOSED. ONE QUESTION ONLY: the
 * listing. Who may build a profile is decided by the Profile Worker (staff only for now),
 * which reads `preview-profile-disabled` (a kill switch that fails open) and never this flag.
 */
export const PROFILE_VISIBLE_FLAG = 'preview-profile-visible';

/**
 * Whether Labs lists AI Detector. A gate, and it fails CLOSED. ONE QUESTION ONLY: the listing.
 * Who may run a check is decided by the AI Detector Worker (staff emails plus
 * `labs-ai-detector-access`), which never reads this flag.
 */
export const AI_DETECTOR_VISIBLE_FLAG = 'labs-ai-detector-visible';

/**
 * Whether Labs lists B-Side, the internal music player at bside.labs.240.org. A gate, and it
 * fails CLOSED. ONE QUESTION ONLY: the listing. Whether someone may play anything is decided
 * by B-Side's own Worker, which reads `preview-b-side-enabled` with a verified Clerk session.
 * The card also requires an internal address on the signed-in identity (BSideListing.tsx),
 * so a broadened flag still cannot list an internal prototype to teachers.
 */
export const B_SIDE_VISIBLE_FLAG = 'preview-b-side-visible';

/** Listing and launch-link readiness are separate fail-closed gates, never app authorization. */
export const JOBS_VISIBLE_FLAG = 'preview-jobs-visible';
export const JOBS_LAUNCH_FLAG = 'preview-jobs-launch-enabled';

/**
 * Whether Labs lists each idea card. Gates that fail CLOSED, like Sites: an idea is not a
 * product, so an unresolved flag must not announce it. Each carries
 * `{"publicRolloutPercent": 0}` so the staff badge reads "Internal only".
 */
export const IDEA_VISIBLE_FLAGS: Record<IdeaId, string> = {
  news: 'preview-news-visible',
};

/**
 * Kill switch, not a gate: it can take the notify CTA away, and its absence never
 * withholds it. `CLAUDE.md` asks which one you built — this is a switch, and it fails
 * OPEN. The Statsig gate it replaces was never created, so the CTA has always shown;
 * creating this flag in PostHog is what makes taking it down possible, not what makes
 * it appear.
 */
export const NOTIFY_SWITCH = 'preview-notify-signup';

/** Fail-open switch for the general updates control alone. */
export const PREVIEW_UPDATES_SWITCH = 'preview-updates-signup';

/** Re-exported so callers have one import for the page's copy. */
export { DEFAULT_COPY } from '../../core/copy';

export interface Analytics {
  watchNotifySwitches?: (onChange: (state: { notify: boolean; updates: boolean }) => void) => () => void;
  watchLandingLayoutVariant?: (onChange: (variant: LandingLayoutVariant | null) => void) => () => void;
  recordLandingLayoutExposure?: (variant: LandingLayoutVariant) => void;
  watchFrontDoorVariant?: (onChange: (variant: FrontDoorVariant | null) => void) => () => void;
  recordFrontDoorExposure?: (variant: FrontDoorVariant) => void;
  watchStampCardVariant?: (onChange: (variant: StampCardVariant | null) => void) => () => void;
  recordStampCardExposure?: (variant: StampCardVariant) => void;
  track: (event: AnalyticsEvent) => void;
  /** Always true now. The adapter no longer awaits a vendor before it can answer. */
  ready: boolean;
  watchOsaVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchObjectivesVisibility?: (onChange: (visible: boolean) => void) => () => void;
  watchRubricVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchSitesVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchMarketplaceVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchWritingFeedbackVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchCollabVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchPlannerVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchProfileVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchAiDetectorVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchBSideVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchJobsVisibility?: (onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  watchJobsLaunch?: (onChange: (enabled: boolean) => void) => () => void;
  watchIdeaVisibility?: (idea: IdeaId, onChange: (visible: boolean, audience?: ListingAudience) => void) => () => void;
  /**
   * Whether PostHog is currently running the suggestion-box survey.
   *
   * There is no feature flag behind the suggestion box: the survey is the switch. Live
   * survey, button on the page; stopped or still a draft, no button — because a button
   * whose survey is not running opens nothing at all.
   */
  watchSuggestionBox?: (onChange: (available: boolean) => void) => () => void;
  /** False only when the switch is explicitly off. Unknown and unresolved both mean on. */
  gate: (name: string) => boolean;
  /**
   * Attaches a signed-in identity to the PostHog person.
   *
   * Without this the visitor is anonymous and `person_profiles: 'identified_only'`
   * creates no profile, so a feature flag targeting a person property — an email, a
   * domain — has nothing to evaluate against and matches nobody.
   *
   * Identifying also reloads the flags, so anything watching a flag hears the
   * re-evaluation without extra wiring.
   */
  identify?: (id: string, properties: { email?: string }) => void;
  /** Nobody is signed in: experiment arms may now be read on the anonymous id. */
  markAnonymous?: () => void;
  /** Drops the identity on sign-out, so the next visitor on this browser is not the last one. */
  reset?: () => void;
  setSubscriptionIdentity?: (id: string | null, getToken?: GetToken) => void;
}

const env = import.meta.env;

// Publishable client credentials shared with sgapp-client. They are bundled only in
// production; tests and local development still default to the null adapter unless an
// explicit VITE_* override is supplied.
export const PUBLIC_POSTHOG_KEY = 'phc_wfBw5f9Vpx4q8ycyGCD3AKJo4JbtY6mfsTr62XSoqUJ3';
export const PUBLIC_POSTHOG_HOST = 'https://s.240.org';

function productionDefault(value: string): string | undefined {
  return env.PROD ? value : undefined;
}

function swallow(label: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    // Deliberately not rethrown. See rule 2 above.
    console.warn(`[analytics] ${label} failed`, error);
  }
}

function initPostHog(): ((event: AnalyticsEvent, timestamp?: Date) => void) | null {
  const key = env.VITE_POSTHOG_KEY || productionDefault(PUBLIC_POSTHOG_KEY);
  if (!key) return null;

  posthog.init(key, {
    api_host: env.VITE_POSTHOG_HOST || PUBLIC_POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    capture_pageview: false,
    // Set here rather than inherited from the project toggle, which the study app shares.
    capture_exceptions: LABS_EXCEPTION_CAPTURE,
    before_send: scrubExceptionEvent,
  });

  return (event, timestamp) => swallow('posthog.capture', () => posthog.capture(event.name, { ...event.properties, environment: ['labs.dev.240.org', 'preview.dev.240.org'].includes(window.location.hostname) ? 'development' : 'production' }, ...(timestamp ? [{ timestamp }] : [])));
}

/** The null implementation. This is what runs whenever the keys are absent. */
export function nullAnalytics(): Analytics {
  return {
    track: () => {},
    ready: true,
    // Open by default. A missing PostHog must not hide the only CTA on the page.
    gate: () => true,
  };
}

export async function createAnalytics(): Promise<Analytics> {
  let capture: ((event: AnalyticsEvent, timestamp?: Date) => void) | null = null;
  const subscription = createSubscriptionEnricher({ hostname: window.location.hostname });
  swallow('posthog.init', () => {
    capture = initPostHog();
  });

  /**
   * Read straight off the initialised client. posthog-js resolves flags asynchronously
   * after init, so an early read can answer `undefined` — which is why the switch below
   * falls back to the permissive value rather than to `false`. A flag that has not
   * arrived yet must not look like a flag that is switched off.
   */
  const flag = (name: string): boolean | string | undefined => {
    if (!capture) return undefined;
    try {
      return posthog.getFeatureFlag(name) ?? undefined;
    } catch {
      return undefined;
    }
  };

  /**
   * A tool-listing gate, watched. Starts closed and only opens on an explicit `true`, so
   * a flag that is slow, missing or errored leaves the tool off the page — the opposite
   * of `gate()` below, which is for switches and falls open.
   */
  const watchVisibility = (name: string, onChange: (visible: boolean) => void): (() => void) => {
    onChange(false);
    if (!capture) return () => {};
    try {
      return posthog.onFeatureFlags((_flags, _variants, context) => {
        onChange(!context?.errorsLoading && posthog.isFeatureEnabled(name, { send_event: false }) === true);
      });
    } catch { return () => {}; }
  };

  const watchListing = (name: string, onChange: (visible: boolean, audience?: ListingAudience) => void): (() => void) => {
    onChange(false, 'unknown');
    if (!capture) return () => {};
    try {
      return posthog.onFeatureFlags((_flags, _variants, context) => {
        try {
          const value = context?.errorsLoading ? undefined : posthog.getFeatureFlag(name, { send_event: false });
          // The gate decides who sees the card. Its payload describes the overall
          // public rollout, even when staff qualify through a separate exception.
          const visible = value === true || (name === RUBRIC_VISIBLE_FLAG && (value === 'limited' || value === 'public'));
          const payload = visible ? posthog.getFeatureFlagPayload(name) : undefined;
          const percentage = payload && typeof payload === 'object' && 'publicRolloutPercent' in payload ? payload.publicRolloutPercent : undefined;
          const audience: ListingAudience = typeof percentage === 'number' && Number.isFinite(percentage) && percentage >= 0 && percentage <= 100
            ? { publicRolloutPercent: percentage } : 'unknown';
          onChange(visible, audience);
        } catch { onChange(false, 'unknown'); }
      });
    } catch { return () => {}; }
  };

  /**
   * Whether the page knows who the visitor is. E10 and E13 assign on the person, so an arm
   * read before then is an answer for an anonymous id the visitor is about to leave: on
   * 2026-10-27, 18% of E10's people had been shown both layouts. Known means a returning
   * visitor PostHog already has as identified, flags reloaded after `identify`, or Clerk
   * saying nobody is signed in. Until then an arm stays unread, and the page's own
   * timeout shows today's page with no exposure.
   */
  let identityKnown = false;
  let identifyPending = false;
  const identitySettled = new Set<() => void>();
  const settleIdentity = () => {
    if (identityKnown) return;
    identityKnown = true;
    identitySettled.forEach(read => read());
  };
  if (capture) swallow('posthog.get_property', () => { identityKnown = posthog.get_property('$user_state') === 'identified'; });

  /** An experiment arm, watched. Anything but a known variant is null: unassigned. */
  const watchVariant = <T extends string>(name: string, variants: readonly string[], onChange: (variant: T | null) => void): (() => void) => {
    if (!capture) { onChange(null); return () => {}; }
    let errorsLoading = false;
    const read = () => {
      if (!identityKnown) return;
      try {
        const value = errorsLoading ? undefined : posthog.getFeatureFlag(name, { send_event: false, fresh: true });
        onChange(typeof value === 'string' && variants.includes(value) ? value as T : null);
      } catch { onChange(null); }
    };
    identitySettled.add(read);
    try {
      const stop = posthog.onFeatureFlags((_flags, _variants, context) => {
        errorsLoading = Boolean(context?.errorsLoading);
        // The first flags after identify settle identity, which reads every watched arm.
        if (identifyPending && !identityKnown) settleIdentity();
        else read();
      });
      return () => { identitySettled.delete(read); stop(); };
    } catch { identitySettled.delete(read); onChange(null); return () => {}; }
  };

  const recordExposure = (name: string, variant: string) => {
    if (!capture || !['labs.240.org', 'preview.240.org'].includes(window.location.hostname)) return;
    // Sent once the assigned page has rendered, never from the flag read itself.
    swallow(`${name} exposure`, () => posthog.capture('$feature_flag_called', {
      $feature_flag: name,
      $feature_flag_response: variant,
      [`$feature/${name}`]: variant,
      environment: 'production',
    }));
  };

  return {
    ready: true,
    watchNotifySwitches: (onChange) => {
      const update = () => onChange({ notify: flag(NOTIFY_SWITCH) !== false, updates: flag(PREVIEW_UPDATES_SWITCH) !== false });
      update();
      if (!capture) return () => {};
      try { return posthog.onFeatureFlags(update); } catch { return () => {}; }
    },
    watchLandingLayoutVariant: (onChange) => watchVariant(LANDING_LAYOUT_FLAG, LANDING_LAYOUT_VARIANTS, onChange),
    recordLandingLayoutExposure: (variant) => recordExposure(LANDING_LAYOUT_FLAG, variant),
    watchFrontDoorVariant: (onChange) => watchVariant(FRONT_DOOR_FLAG, FRONT_DOOR_VARIANTS, onChange),
    recordFrontDoorExposure: (variant) => recordExposure(FRONT_DOOR_FLAG, variant),
    watchStampCardVariant: (onChange) => watchVariant(STAMP_CARD_FLAG, STAMP_CARD_VARIANTS, onChange),
    recordStampCardExposure: (variant) => recordExposure(STAMP_CARD_FLAG, variant),

    watchOsaVisibility: (onChange) => watchListing(OSA_VISIBLE_FLAG, onChange),

    watchObjectivesVisibility: (onChange) => watchVisibility(OBJECTIVES_VISIBLE_FLAG, onChange),

    watchRubricVisibility: (onChange) => watchListing(RUBRIC_VISIBLE_FLAG, onChange),

    watchSitesVisibility: (onChange) => watchListing(SITES_VISIBLE_FLAG, onChange),

    watchMarketplaceVisibility: (onChange) => watchListing(MARKETPLACE_VISIBLE_FLAG, onChange),

    watchWritingFeedbackVisibility: (onChange) => watchListing(WRITING_FEEDBACK_VISIBLE_FLAG, onChange),
    watchCollabVisibility: (onChange) => watchListing(COLLAB_VISIBLE_FLAG, onChange),
    watchPlannerVisibility: (onChange) => watchListing(PLANNER_VISIBLE_FLAG, onChange),
    watchProfileVisibility: (onChange) => watchListing(PROFILE_VISIBLE_FLAG, onChange),
    watchAiDetectorVisibility: (onChange) => watchListing(AI_DETECTOR_VISIBLE_FLAG, onChange),
    watchBSideVisibility: (onChange) => watchListing(B_SIDE_VISIBLE_FLAG, onChange),
    watchJobsVisibility: (onChange) => watchListing(JOBS_VISIBLE_FLAG, onChange),
    watchJobsLaunch: (onChange) => watchVisibility(JOBS_LAUNCH_FLAG, onChange),
    watchIdeaVisibility: (idea, onChange) => watchListing(IDEA_VISIBLE_FLAGS[idea], onChange),

    /**
     * `onSurveysLoaded` first, then ask. `getActiveMatchingSurveys` answers nothing at
     * all when the surveys script has not finished loading — it warns and returns
     * without ever calling the callback — so asking once on mount is a coin flip on
     * network timing. Subscribing gets the answer whenever it arrives, and again if the
     * definitions are refreshed.
     */
    watchSuggestionBox: (onChange) => {
      onChange(false);
      if (!capture) return () => {};

      let live = true;
      let unsubscribe = () => {};

      swallow('posthog.onSurveysLoaded', () => {
        unsubscribe = posthog.onSurveysLoaded(() => {
          posthog.getActiveMatchingSurveys((surveys) => {
            if (live) onChange(hasSuggestionBox(surveys));
          });
        });
      });

      return () => {
        live = false;
        unsubscribe();
      };
    },

    identify: (id, properties) => {
      if (!capture) return;
      // The next flags answer is the identified one; arms are read from it.
      if (!identityKnown) identifyPending = true;
      swallow('posthog.identify', () => posthog.identify(id, properties));
    },

    markAnonymous: settleIdentity,

    reset: () => {
      subscription.dispose();
      if (!capture) return;
      swallow('posthog.reset', () => posthog.reset());
    },

    setSubscriptionIdentity: subscription.setIdentity,
    track: (event) => {
      if (capture) subscription.track(event, (enriched, timestamp) => capture?.({ ...event, properties: enriched.properties }, timestamp));
    },

    // Off only when PostHog says so explicitly. A missing, slow or unresolved flag
    // leaves the CTA up — see NOTIFY_SWITCH.
    gate: (name) => flag(name) !== false,
  };
}
